import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { BrandLockup, PoweredByFundle } from "../components/Brand";

const DEMO_ACCOUNTS = [
  { label: "Group HR Head", email: "grouphr@talentos.ai" },
  { label: "Recruiter", email: "recruiter@talentos.ai" },
  { label: "Hiring Manager", email: "hiringmanager@talentos.ai" },
  { label: "Finance Approver", email: "finance@talentos.ai" },
  { label: "Interview Panelist", email: "panelist@talentos.ai" },
  { label: "Candidate", email: "candidate@talentos.ai" },
  { label: "Super Admin", email: "superadmin@talentos.ai" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (em, pw) => {
    setBusy(true);
    setError("");
    try {
      await login(em, pw);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden"
        style={{ backgroundColor: "#0A251D" }}>
        <img src="https://images.unsplash.com/photo-1704975986930-0c09f513c985?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(10,37,29,0.55) 0%, rgba(10,37,29,0.92) 70%)" }} />
        <div className="relative"><BrandLockup light /></div>
        <div className="relative">
          <h1 className="font-heading font-black text-4xl xl:text-5xl leading-[1.05] tracking-tighter">
            The Recruitment<br />Command Center<br />for the <span className="text-[#E05A47]">ICS Group</span>.
          </h1>
          <p className="mt-6 text-[#C9DCD0] max-w-md leading-relaxed">
            Requisitions, AI screening, intelligent candidate assistance, interviews,
            offers and onboarding — one governed platform across every entity.
          </p>
        </div>
        <div className="relative"><PoweredByFundle light /></div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md rise-in">
          <div className="lg:hidden mb-8">
            <BrandLockup />
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Use your workspace credentials or a demo account.</p>

          <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Email</label>
              <input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border bg-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A251D]"
                placeholder="you@talentos.ai" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Password</label>
              <input data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border bg-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A251D]"
                placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2">{error}</div>}
            <button data-testid="login-submit-button" disabled={busy}
              className="w-full bg-[#0A251D] text-white py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">One-click demo roles (password auto-filled)</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((d) => (
                <button key={d.email} data-testid={`demo-login-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => doLogin(d.email, "Demo@123")}
                  className="border bg-white rounded-xl px-3 py-2 text-xs text-left hover:border-[#0A251D] hover:text-[#0A251D] transition-colors duration-150">
                  {d.label}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">All demo accounts use password <code className="bg-secondary px-1">Demo@123</code></div>
            <a href="/careers" data-testid="browse-careers-link"
              className="mt-6 block text-center border border-[#0A251D] text-[#0A251D] py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#0A251D] hover:text-white transition-colors duration-150">
              Browse open roles — Careers Portal →
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/platform-doc`}
               target="_blank" rel="noreferrer" data-testid="platform-doc-link"
               className="mt-2 block text-center text-[11px] uppercase tracking-widest text-muted-foreground hover:text-[#0A251D] transition-colors duration-150">
              Download the platform overview (PDF)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
